/**
 * Polling Plugin for Vue.js
 *
 * Provides a global polling utility that can poll an API endpoint
 * with configurable timeout and interval settings.
 */

class PollingManager {
  constructor() {
    this.activePolls = new Map();
  }

  /**
   * Start polling an API endpoint
   *
   * @param {String} pollId - Unique identifier for this polling operation
   * @param {Function} pollFunction - Async function to poll (should return response data)
   * @param {Object} options - Polling configuration options
   * @param {Number} options.timeout - Maximum polling duration in milliseconds
   *   (default: 300000 = 5 minutes)
   * @param {Number} options.interval - Interval between polls in milliseconds
   *   (default: 3000 = 3 seconds)
   * @param {Function} options.onSuccess - Callback when polling succeeds
   * @param {Function} options.onError - Callback when polling fails or times out
   * @param {Function} options.onProgress - Optional callback for progress updates
   * @param {Function} options.validateResponse - Function to validate if response is successful
   * @returns {Promise} - Resolves with success data or rejects with error
   */
  async startPolling(pollId, pollFunction, options = {}) {
    const {
      timeout = 300000, // 5 minutes default
      interval = 3000, // 3 seconds default
      onSuccess = () => {},
      onError = () => {},
      onProgress = () => {},
      validateResponse = (data) => data && data.status === 'completed',
    } = options;

    // Stop any existing poll with the same ID
    this.stopPolling(pollId);

    const startTime = Date.now();
    let pollCount = 0;
    let timeoutId = null;
    let intervalId = null;
    let isActive = true;

    const cleanup = () => {
      isActive = false;
      if (timeoutId) clearTimeout(timeoutId);
      if (intervalId) clearInterval(intervalId);
      this.activePolls.delete(pollId);
    };

    // Store poll control
    this.activePolls.set(pollId, { cleanup, startTime });

    return new Promise((resolve, reject) => {
      // Set overall timeout
      timeoutId = setTimeout(() => {
        cleanup();
        const error = new Error('Polling timeout exceeded');
        error.code = 'POLLING_TIMEOUT';
        onError(error);
        reject(error);
      }, timeout);

      // Poll function
      const poll = async () => {
        if (!isActive) return;

        try {
          pollCount += 1;
          const elapsedTime = Date.now() - startTime;
          const remainingTime = timeout - elapsedTime;

          // Progress callback
          onProgress({
            pollCount,
            elapsedTime,
            remainingTime,
            progress: Math.min((elapsedTime / timeout) * 100, 100),
          });

          // Execute the polling function
          const response = await pollFunction();

          // Check if response indicates success
          if (validateResponse(response)) {
            cleanup();
            onSuccess(response);
            resolve(response);
          }
        } catch (error) {
          // Continue polling on error unless it's a critical error
          if (error.response && error.response.status >= 400 && error.response.status < 500) {
            cleanup();
            onError(error);
            reject(error);
          }
          // Otherwise, continue polling
        }
      };

      // Start immediate first poll
      poll();

      // Set interval for subsequent polls
      intervalId = setInterval(poll, interval);
    });
  }

  /**
   * Stop an active polling operation
   *
   * @param {String} pollId - Unique identifier for the polling operation to stop
   */
  stopPolling(pollId) {
    const poll = this.activePolls.get(pollId);
    if (poll && poll.cleanup) {
      poll.cleanup();
    }
  }

  /**
   * Stop all active polling operations
   */
  stopAllPolling() {
    this.activePolls.forEach((poll) => {
      if (poll.cleanup) poll.cleanup();
    });
    this.activePolls.clear();
  }

  /**
   * Check if a polling operation is active
   *
   * @param {String} pollId - Unique identifier for the polling operation
   * @returns {Boolean}
   */
  isPolling(pollId) {
    return this.activePolls.has(pollId);
  }
}

// Vue plugin
export default {
  install(Vue) {
    const pollingManager = new PollingManager();

    // Add to Vue prototype for global access
    // eslint-disable-next-line no-param-reassign
    Vue.prototype.$polling = pollingManager;

    // Cleanup on app unmount
    Vue.mixin({
      beforeDestroy() {
        // Optional: Stop all polls when component is destroyed
        // pollingManager.stopAllPolling();
      },
    });
  },
};
